import { FC, useMemo } from 'react';
import { Checkbox, Form, Modal, Select, Tooltip } from '@maxtropy/components';
import { Row, Col } from 'antd';
import dayjs from 'dayjs';

const { Option } = Select;

interface CopyToMonthModalProps {
  visible: boolean;
  onCancel: () => void;
  onOk: (year: number, months: number[]) => void;
  contractStartDate?: string;
  contractEndDate?: string;
}

const CopyToMonthModal: FC<CopyToMonthModalProps> = ({
  visible,
  onCancel,
  onOk,
  contractStartDate,
  contractEndDate,
}) => {
  const [form] = Form.useForm();
  const selectedYear = Form.useWatch('year', form);
  const selectedMonths = Form.useWatch('months', form);

  // 生成年份选项（根据合同有效期）
  const yearOptions = useMemo(() => {
    const years: number[] = [];
    if (contractStartDate && contractEndDate) {
      const startYear = dayjs(contractStartDate).year();
      const endYear = dayjs(contractEndDate).year();
      for (let year = startYear; year <= endYear; year++) {
        years.push(year);
      }
    } else {
      // 默认显示当前年份及前后两年
      const currentYear = dayjs().year();
      for (let year = currentYear - 2; year <= currentYear + 2; year++) {
        years.push(year);
      }
    }
    return years;
  }, [contractStartDate, contractEndDate]);

  // 月份列表
  const monthList = [
    { label: '1月', value: 1 },
    { label: '2月', value: 2 },
    { label: '3月', value: 3 },
    { label: '4月', value: 4 },
    { label: '5月', value: 5 },
    { label: '6月', value: 6 },
    { label: '7月', value: 7 },
    { label: '8月', value: 8 },
    { label: '9月', value: 9 },
    { label: '10月', value: 10 },
    { label: '11月', value: 11 },
    { label: '12月', value: 12 },
  ];

  // 判断月份是否在合同有效期内
  const isMonthInContract = (year: number, month: number) => {
    if (!contractStartDate || !contractEndDate) return true;
    const monthStart = dayjs(`${year}-${month}-01`);
    const monthEnd = monthStart.endOf('month');
    const contractStart = dayjs(contractStartDate).startOf('date');
    const contractEnd = dayjs(contractEndDate).endOf('date');
    // 月份与合同期有交集
    return monthEnd.isSameOrAfter(contractStart) && monthStart.isSameOrBefore(contractEnd);
  };

  // 全选
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      // 只选择有效月份
      const validMonths = monthList
        .filter(m => isMonthInContract(selectedYear || yearOptions[0], m.value))
        .map(m => m.value);
      form.setFieldValue('months', validMonths);
    } else {
      form.setFieldValue('months', []);
    }
  };

  // 判断是否全选
  const isAllSelected = useMemo(() => {
    if (!selectedMonths || selectedMonths.length === 0) return false;
    const validMonths = monthList
      .filter(m => isMonthInContract(selectedYear || yearOptions[0], m.value))
      .map(m => m.value);
    return validMonths.length === selectedMonths.length && validMonths.every(m => selectedMonths.includes(m));
  }, [selectedMonths, selectedYear, yearOptions]);

  // 判断是否半选（部分选中）
  const isIndeterminate = useMemo(() => {
    if (!selectedMonths || selectedMonths.length === 0) return false;
    const validMonths = monthList
      .filter(m => isMonthInContract(selectedYear || yearOptions[0], m.value))
      .map(m => m.value);
    return selectedMonths.length > 0 && selectedMonths.length < validMonths.length;
  }, [selectedMonths, selectedYear, yearOptions]);

  // 年份变化时，过滤无效月份
  const handleYearChange = (year: number) => {
    const validMonths = (selectedMonths || []).filter((m: number) => isMonthInContract(year, m));
    form.setFieldValue('months', validMonths);
  };

  // 确认
  const handleOk = () => {
    form.validateFields().then(values => {
      onOk(values.year, values.months);
      form.resetFields();
    });
  };

  // 取消
  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      title="复制到所选月"
      open={visible}
      onCancel={handleCancel}
      onOk={handleOk}
      maskClosable={false}
      contentClassName="modal-form-content"
    >
      <Form form={form} layout="horizontal" initialValues={{ year: yearOptions[0], months: [] }}>
        <Form.Item label="复制到的日期" name="year" rules={[{ required: true, message: '请选择年份' }]}>
          <Select style={{ width: 120 }} placeholder="请选择年份" onChange={handleYearChange}>
            {yearOptions.map(year => (
              <Option key={year} value={year}>
                {year}年
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item>
          <div style={{ marginBottom: 8 }}>
            <Checkbox
              checked={isAllSelected}
              indeterminate={isIndeterminate}
              onChange={e => handleSelectAll(e.target.checked)}
            >
              全选
            </Checkbox>
          </div>
          <Form.Item name="months" rules={[{ required: true, message: '请选择月份' }]}>
            <Checkbox.Group style={{ width: '100%' }}>
              <Row>
                {monthList.map(month => {
                  const disabled = !isMonthInContract(selectedYear || yearOptions[0], month.value);
                  const checkbox = (
                    <Checkbox value={month.value} disabled={disabled}>
                      {month.label}
                    </Checkbox>
                  );
                  return (
                    <Col span={6} key={month.value}>
                      {disabled ? <Tooltip title="不在合同有效期内">{checkbox}</Tooltip> : checkbox}
                    </Col>
                  );
                })}
              </Row>
            </Checkbox.Group>
          </Form.Item>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CopyToMonthModal;
